import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { isGatewayPath } from '@/routes/gateway/gatewayPaths'
import { isSameCoachBoardFocus } from './coachBoardFocus'
import { CoachLauncherPlacement } from './CoachLauncherPlacement'
import { CoachNavigation } from './CoachNavigation'
import { CoachScreenRules } from './CoachScreenRules'
import type { CoachBoardFocus, CoachGameContext, CoachMessage } from './coach.types'

export type CoachGameContextFactory = () => CoachGameContext | Promise<CoachGameContext>

type CoachContextValue = {
  isOpen: boolean
  setOpen: (open: boolean) => void
  messages: CoachMessage[]
  setMessages: React.Dispatch<React.SetStateAction<CoachMessage[]>>
  gameContext?: CoachGameContext
  setGameContext: (context?: CoachGameContext) => void
  prepareGameContext?: CoachGameContextFactory
  setGameContextFactory: (factory?: CoachGameContextFactory) => void
  boardFocus?: CoachBoardFocus
  pinnedBoardFocus?: CoachBoardFocus
  previewBoardFocus: (focus?: CoachBoardFocus) => void
  toggleBoardFocus: (focus: CoachBoardFocus) => void
  clearBoardFocus: () => void
  route: string
  screen: string
}

const CoachContext = createContext<CoachContextValue | null>(null)

function screenFromRoute(route: string): string {
  if (route.startsWith('/strategy')) return 'strategy'
  if (route.startsWith('/rules')) return 'rules'
  if (route.startsWith('/challenges')) return 'challenges'
  if (route.startsWith('/online')) return 'online'
  if (route.startsWith('/hub')) return 'hub'
  if (route === '/home') return 'home'
  return 'other'
}

export function CoachProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const [isOpen, setOpen] = useState(false)
  const [messages, setMessages] = useState<CoachMessage[]>([])
  const [gameContext, setGameContextState] = useState<CoachGameContext>()
  const [prepareGameContext, setPrepareGameContext] = useState<CoachGameContextFactory>()
  const [previewedBoardFocus, previewBoardFocus] = useState<CoachBoardFocus>()
  const [pinnedBoardFocus, setPinnedBoardFocus] = useState<CoachBoardFocus>()
  const clearBoardFocus = useCallback(() => {
    previewBoardFocus(undefined)
    setPinnedBoardFocus(undefined)
  }, [])
  const toggleBoardFocus = useCallback((focus: CoachBoardFocus) => {
    previewBoardFocus(undefined)
    setPinnedBoardFocus(current => (isSameCoachBoardFocus(current, focus) ? undefined : focus))
  }, [])
  const setGameContext = useCallback((context?: CoachGameContext) => {
    setGameContextState(context)
    if (!context) {
      previewBoardFocus(undefined)
      setPinnedBoardFocus(undefined)
    }
  }, [])
  const setGameContextFactory = useCallback((factory?: CoachGameContextFactory) => {
    setPrepareGameContext(() => factory)
  }, [])
  const route = location.pathname
  const isGateway = isGatewayPath(route)

  useEffect(() => clearBoardFocus(), [clearBoardFocus, route])

  useEffect(() => {
    if (isGateway) setOpen(false)
  }, [isGateway])

  const value = useMemo(
    () => ({
      isOpen: !isGateway && isOpen,
      setOpen,
      messages,
      setMessages,
      gameContext,
      setGameContext,
      prepareGameContext,
      setGameContextFactory,
      boardFocus: previewedBoardFocus ?? pinnedBoardFocus,
      pinnedBoardFocus,
      previewBoardFocus,
      toggleBoardFocus,
      clearBoardFocus,
      route,
      screen: screenFromRoute(route),
    }),
    [
      clearBoardFocus,
      gameContext,
      isGateway,
      isOpen,
      messages,
      prepareGameContext,
      pinnedBoardFocus,
      previewedBoardFocus,
      route,
      setGameContext,
      setGameContextFactory,
      toggleBoardFocus,
    ],
  )

  return (
    <CoachContext.Provider value={value}>
      <CoachNavigation>
        <CoachScreenRules>
          <CoachLauncherPlacement>{children}</CoachLauncherPlacement>
        </CoachScreenRules>
      </CoachNavigation>
    </CoachContext.Provider>
  )
}

export function useCoach() {
  const context = useContext(CoachContext)
  if (!context) throw new Error('useCoach must be used inside CoachProvider')
  return context
}

export function useOptionalCoach() {
  return useContext(CoachContext)
}
